import { createAction, createSlice } from "@reduxjs/toolkit";
import { jetPumpInput } from "../../model/jetlift/jetlift";
import { generateRange } from "../../../utilities/commonUtilities";

interface jetPumpState {
    loading?: boolean; 
    error?: string | null;
    jetPump: jetPumpInput | null;
    tangent: string | null;
    darcey: string | null;
};

export const initialState: jetPumpState = {
    loading: false,
    error: null,
    jetPump: null,
    tangent: "",
    darcey: "",
};

export const getNozzleArea = createAction("jetlift/jetPump/getNozzleArea");
export const getThroatArea = createAction("jetlift/jetPump/getThroatArea");

const JetPumpSlice = createSlice({
    name: "jetlift/jetPump",
    initialState,
    reducers: {
        resetJetPumpInput: () => ({
            ...initialState,
        }),
        setLoading: (state, action) => {
            state.loading = action?.payload;
        },
        setError: (state, action) => {
            state.error = action?.payload;
        },
        setJetPumpInput: (state, action) => {
            const payload = action?.payload;
            if (payload) {
                state.jetPump = {
                    ...payload,
                    manufacturer: payload?.manufacturer ? payload?.manufacturer : "0",
                    manufactureList: payload.manufactureList ? Object?.keys(payload?.manufactureList)?.map((key) => ({
                        value: key,
                        label: payload?.manufactureList[key],
                    })) : [],
                    nozzleList: payload.nozzleList ? Object?.keys(payload?.nozzleList)?.map((key) => ({
                        value: payload?.nozzleList[key],
                        label: key,
                    })) : [],
                    throatList: payload.throatList ? Object?.keys(payload?.throatList)?.map((key) => ({
                        value: payload?.throatList[key],
                        label: key,
                    })) : [],
                    powerFluidTypeList: payload.powerFluidTypeList ? Object?.keys(payload?.powerFluidTypeList)?.map((key) => ({
                        value: payload?.powerFluidTypeList[key],
                        label: key,
                    })) : [],
                    outputPressures: (payload?.minFluidRange > 0 && payload?.maxFluidRange > 0) ? generateRange(+payload?.minFluidRange, +payload?.maxFluidRange, +payload?.stepFluidRange)?.join(", ") || "" : "",
                }
            }
        },
        setManufacturerId: (state, action) => {
            if (state?.jetPump) {
                state.jetPump = {
                    ...state.jetPump,
                    manufacturerId: action?.payload
                };
            }
        },
        setManufacturerName: (state, action) => {
            if (state?.jetPump) {
                state.jetPump = {
                    ...state.jetPump,
                    manufacturerName: action?.payload
                };
            }
        },
        setMinPowerFluidPressure: (state, action) => {
            if (state?.jetPump) {
                state.jetPump = {
                    ...state.jetPump,
                    minFluidRange: action?.payload
                };
            }
        },
        setMaxPowerFLuidPressure: (state, action) => {
            if (state?.jetPump) {
                state.jetPump = {
                    ...state.jetPump,
                    maxFluidRange: action?.payload
                };
            }
        },
        setStepFluidRange: (state, action) => {
            if (state?.jetPump) {
                state.jetPump = {
                    ...state.jetPump,
                    stepFluidRange: action?.payload
                };
            }
        },
        setNozzleDiameterSize: (state, action) => {
            if (state?.jetPump) {
                state.jetPump = {
                    ...state.jetPump,
                    nozzleDiameterSize: action?.payload
                };
            }
        },
        setNozzleAreaSize: (state, action) => {
            if (state?.jetPump) {
                state.jetPump = {
                    ...state.jetPump,
                    nozzleAreaSize: action?.payload
                };
            }
        },
        setNozzleValue: (state, action) => {
            if (state?.jetPump) {
                state.jetPump = {
                    ...state.jetPump,
                    nozzleValue: action?.payload
                };
            }
        },
        setThroatDiameterSize: (state, action) => {
            if (state?.jetPump) {
                state.jetPump = {
                    ...state.jetPump,
                    throatDiameterSize: action?.payload
                };
            }
        },
        setThroatAreaSize: (state, action) => {
            if (state?.jetPump) {
                state.jetPump = {
                    ...state.jetPump,
                    throatAreaSize: action?.payload
                };
            }
        },
        setThroatValue: (state, action) => {
            if (state?.jetPump) {
                state.jetPump = {
                    ...state.jetPump,
                    throatValue: action?.payload
                };
            }
        },
        setPowerFluidType: (state, action) => {
            if (state?.jetPump) {
                state.jetPump = {
                    ...state.jetPump,
                    powerFluidType: action?.payload
                };
            };
        },
        setPowerFluidDensity: (state, action) => {
            if (state?.jetPump) {
                state.jetPump = {
                    ...state.jetPump,
                    powerFluidDensity: action?.payload
                };
            }
        },
        setOutputPressure: (state, action) => {
            if (state?.jetPump) {
                state.jetPump = {
                    ...state.jetPump,
                    outputPressures: action?.payload
                };
            }
        },
        setGasVent: (state, action) => {
            if (state?.jetPump) {
                state.jetPump = {
                    ...state.jetPump,
                    gasVent: action?.payload
                };
            }
        },
        setTangent: (state, action) => {
            state.tangent = action?.payload;
        },
        setDarcey: (state, action) => {
            state.darcey = action?.payload;
        },
        setSelectedVesselSize: (state, action) => {
            if (state.jetPump) {
                state.jetPump = {
                    ...state.jetPump,
                    selected_vessel_size: action?.payload
                };
            }
        },
        setSelectedChargePumpSize: (state, action) => {
            if (state.jetPump) {
                state.jetPump = {
                    ...state.jetPump,
                    selected_charge_pump_size: action?.payload
                };
            }
        },
        setSelectedSurfacePumpSize: (state, action) => {
            if (state.jetPump) {
                state.jetPump = {
                    ...state.jetPump,
                    selected_surface_pump_size: action?.payload
                };
            }
        },
        setSelectedPlungerSize: (state, action) => {
            if (state.jetPump) {
                state.jetPump = {
                    ...state.jetPump,
                    selected_plunget_size: action?.payload
                };
            }
        },
        setJetPumpSize: (state, action) => {
            if (state.jetPump) {
                state.jetPump = {
                    ...state.jetPump,
                    schematics_jet_pump_size: action?.payload
                };
            }
        },
        setNozzleList: (state, action) => {
            if (state.jetPump) {
                state.jetPump = {
                    ...state.jetPump,
                    nozzleList: action?.payload?.length > 0 ? action?.payload?.map((item: any) => ({ label: item?.displayName, value: item?.diameter?.toString() })) : [],
                };
            }
        },
        setThroatList: (state, action) => {
            if (state.jetPump) {
                state.jetPump = {
                    ...state.jetPump,
                    throatList: action?.payload?.length > 0 ? action?.payload?.map((item: any) => ({ label: item?.displayName, value: item?.diameter?.toString() })) : [],
                };
            }
        },
        clearNozzleAndThroat: (state) => {
            if (state.jetPump) {
                state.jetPump = {
                    ...state.jetPump,
                    nozzleDiameterSize: "",
                    nozzleAreaSize: "",
                    nozzleList: [],
                    throatDiameterSize: "",
                    throatAreaSize: "",
                    throatList: [],
                };
            }
        },
    }
});

export const {
    resetJetPumpInput,
    setLoading,
    setError,
    setJetPumpInput,
    setManufacturerId,
    setManufacturerName,
    setMinPowerFluidPressure,
    setMaxPowerFLuidPressure,
    setStepFluidRange,
    setNozzleDiameterSize,
    setNozzleAreaSize,
    setNozzleValue,
    setThroatDiameterSize,
    setThroatAreaSize,
    setThroatValue,
    setPowerFluidType,
    setPowerFluidDensity,
    setOutputPressure,
    setGasVent,
    setTangent,
    setDarcey,
    setSelectedVesselSize,
    setSelectedChargePumpSize,
    setSelectedSurfacePumpSize,
    setSelectedPlungerSize,
    setJetPumpSize,
    setNozzleList,
    setThroatList,
    clearNozzleAndThroat,
} = JetPumpSlice.actions;

export default JetPumpSlice.reducer;